import React, { Fragment, useRef } from 'react'
import { Link, Form } from '@remix-run/react'
import { Disclosure, Menu, Transition } from '@headlessui/react'
import { Bars3Icon, BellIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Logo } from '~/components'
import { classNames } from '~/utils/client/helpers'
import { LinkToPostfach, useUrlToPostfach } from './LinkToPostfach'
import { LinkToDokumentcenter, useUrlToDokumentcenter } from './LinkToDokumentcenter'
import { LinkToAnlegerfragebogen, useUrlToAnlegerfragebogen } from './LinkToAnlegerfragebogen'
import { useFocusTrap, useInertAppContent } from '~/utils/useFocusTrap'
import { i18n } from '@lingui/core'

export type NavBarLinkItem = {
  name: string
  href: string
  isActive: boolean
}

interface PrimaryNavbarProps {
  user: any
  showDevBanner?: boolean
}

// Shared header for nav and mobile menu
function NavbarHeader({ button }: { button: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-4 py-2 h-16 border-b border-border-subtle bg-white">
      <Link to="/kundenzugang" aria-label="Kundenzugang">
        <Logo className="h-8" />
      </Link>
      {button}
    </div>
  )
}

type MobileMenuProps = {
  open: boolean
  close: () => void
  user: PrimaryNavbarProps['user']
  isDev: boolean
  image: string
  postfachUrl: string
  dokumentcenterUrl: string
  anlegerfragebogenUrl: string
}

function MobileMenu({
  close,
  user,
  isDev,
  image,
  postfachUrl,
  dokumentcenterUrl,
  anlegerfragebogenUrl,
  open,
}: MobileMenuProps) {
  const trapRef = useRef<HTMLDivElement>(null)

  useInertAppContent(open)
  useFocusTrap(trapRef, open, { onEscape: close })

  if (!open) return null

  return (
    <Disclosure.Panel static as={Fragment}>
      <div ref={trapRef} className="fixed inset-0 z-40 sm:hidden">
        <button
          type="button"
          className="absolute inset-0 bg-black bg-opacity-60"
          onClick={close}
          tabIndex={-1}
          aria-hidden="true"
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label={i18n._('Hauptmenü')}
          className="relative z-50 flex max-h-[100dvh] flex-col overflow-hidden rounded-b-xl bg-white shadow-lg"
        >
          <NavbarHeader
            button={
              <Disclosure.Button
                className="text-muted hover:text-secondary focus-ring-brand-inset"
                aria-label={i18n._('Menü schließen')}
              >
                <XMarkIcon className="h-7 w-7" />
              </Disclosure.Button>
            }
          />
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-4">
            <div className="flex items-center px-2">
              <div className="flex-shrink-0">
                <img className="h-10 w-10 rounded-full" src={image} alt="" />
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-heading">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-sm font-medium text-secondary">{user.email ?? ''}</div>
              </div>
              <Link
                to={postfachUrl}
                onClick={close}
                className="relative ml-auto flex-shrink-0 bg-white p-1 text-muted hover:text-secondary focus-ring-brand rounded-full"
              >
                <BellIcon className="h-6 w-6" aria-hidden="true" />
                <span className="absolute -inset-1.5" />
                <span className="sr-only">Postfach ansehen</span>
              </Link>
            </div>
            <div className="mt-3 space-y-1">
              <Link
                to={dokumentcenterUrl}
                onClick={close}
                className="block px-4 py-2 text-base text-secondary hover:bg-surface-hover hover:text-heading focus-ring-brand"
              >
                {i18n._('Meine Dokumente')}
              </Link>
              <Link
                to="/kundenzugang/profil"
                onClick={close}
                className="block px-4 py-2 text-base text-secondary hover:bg-surface-hover hover:text-heading focus-ring-brand"
              >
                {i18n._('Profileinstellungen')}
              </Link>
              {isDev && (
                <Link
                  to={anlegerfragebogenUrl}
                  onClick={close}
                  className="block px-4 py-2 text-base text-secondary hover:bg-surface-hover hover:text-heading focus-ring-brand"
                >
                  {i18n._('Anlegerfragebogen')}
                </Link>
              )}
              <Form method="post" action="/kundenzugang/logout">
                <button
                  className="block px-4 py-2 w-full text-left text-base text-secondary hover:bg-surface-hover hover:text-heading focus-ring-brand"
                  type="submit"
                >
                  {i18n._('Abmelden')}
                </button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </Disclosure.Panel>
  )
}

export function PrimaryNavbar({ user, showDevBanner = false }: PrimaryNavbarProps) {
  const isDev = showDevBanner
  const image = `https://ui-avatars.com/api/?name=${user.firstName ?? ''}+${
    user.lastName
  }&background=D7E7F6`
  const postfachUrl = useUrlToPostfach()
  const dokumentcenterUrl = useUrlToDokumentcenter()
  const anlegerfragebogenUrl = useUrlToAnlegerfragebogen()

  const DesktopMenu = () => (
    <div className="hidden sm:ml-6 sm:flex sm:items-center">
      <LinkToPostfach className="relative bg-white p-1 text-muted hover:text-secondary focus-ring-brand rounded-full">
        <span className="absolute -inset-1.5" />
        <span className="sr-only">Postfach ansehen</span>
        <BellIcon className="h-6 w-6" aria-hidden="true" />
      </LinkToPostfach>

      <Menu as="div" className="relative ml-3">
        <div>
          <Menu.Button className="relative flex max-w-xs items-center bg-white text-sm focus-ring-brand rounded-full">
            <span className="absolute -inset-1.5" />
            <span className="sr-only">Open user menu</span>
            <img className="h-8 w-8 rounded-full" src={image} alt="profile" />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <Menu.Item>
              {({ active }) => (
                <LinkToDokumentcenter
                  className={classNames(
                    active ? 'bg-surface-hover' : '',
                    'block px-4 py-2 text-sm text-body w-full text-left focus-ring-brand'
                  )}
                >
                  {i18n._('Meine Dokumente')}
                </LinkToDokumentcenter>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/kundenzugang/profil"
                  className={classNames(
                    active ? 'bg-surface-hover' : '',
                    'block px-4 py-2 text-sm text-body w-full text-left focus-ring-brand'
                  )}
                >
                  {i18n._('Profileinstellungen')}
                </Link>
              )}
            </Menu.Item>
            {isDev && (
              <Menu.Item>
                {({ active }) => (
                  <LinkToAnlegerfragebogen
                    className={classNames(
                      active ? 'bg-surface-hover' : '',
                      'block px-4 py-2 text-sm text-body w-full text-left focus-ring-brand'
                    )}
                  >
                    {i18n._('Anlegerfragebogen')}
                  </LinkToAnlegerfragebogen>
                )}
              </Menu.Item>
            )}
            <Form method="post" action="/kundenzugang/logout">
              <Menu.Item>
                {({ active }) => (
                  <button
                    name="logout"
                    className={classNames(
                      active ? 'bg-surface-hover' : '',
                      'block px-4 py-2 text-sm text-body w-full text-left focus-ring-brand'
                    )}
                  >
                    {i18n._('Abmelden')}
                  </button>
                )}
              </Menu.Item>
            </Form>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  )

  return (
    <Disclosure
      as="nav"
      className="border-b border-border-default bg-white sticky top-0 z-20 w-full [@media(max-height:400px)]:static"
    >
      {({ open, close }) => (
        <>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10 2xl:px-0">
            <div className="flex h-16 justify-between">
              {/* Desktop menu */}
              <div className="flex">
                <div className="flex flex-shrink-0 items-center">
                  <Link to="/kundenzugang" aria-label="Kundenzugang">
                    <Logo />
                  </Link>
                </div>
              </div>
              <DesktopMenu />
              {/* Mobile only */}
              <div className="-mr-2 flex items-center sm:hidden">
                <Disclosure.Button
                  tabIndex={open ? -1 : 0}
                  aria-expanded={open}
                  aria-haspopup="dialog"
                  className="relative inline-flex items-center justify-center bg-white p-2 text-muted hover:bg-surface-hover hover:text-secondary focus-ring-brand"
                >
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>
          <MobileMenu
            open={open}
            close={close}
            user={user}
            isDev={isDev}
            image={image}
            postfachUrl={postfachUrl}
            dokumentcenterUrl={dokumentcenterUrl}
            anlegerfragebogenUrl={anlegerfragebogenUrl}
          />
        </>
      )}
    </Disclosure>
  )
}
